// @ts-nocheck
import React from 'react';
import { Modal, Tag, Empty, Divider } from 'antd';
import {
  BankOutlined,
  CreditCardOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { colors } from '../design-system/tokens';

interface Transaction {
  id: string;
  transactionType: 'INVOICE' | 'PAYMENT' | 'CREDIT_NOTE';
  direction: 'BUYER_RECEIVABLE' | 'SUPPLIER_PAYABLE' | 'COMMISSION';
  amount: number | string;
  date: string;
  dueDate?: string;
  paymentMethod?: 'WIRE' | 'CHECK' | 'ACH' | null;
  reference?: string;
  description?: string;
  status: 'PENDING' | 'PAID' | 'PARTIAL' | 'VOIDED';
  legacyTable?: string;
}

interface TransactionDetailModalProps {
  open: boolean;
  onClose: () => void;
  transactions: Transaction[];
  shipmentRef?: string;
}

const DIRECTION_CONFIG = {
  BUYER_RECEIVABLE: {
    label: 'Buyer Receivables',
    color: colors.status.positive,
    bgColor: '#f0faf4',
  },
  SUPPLIER_PAYABLE: {
    label: 'Supplier Payables',
    color: colors.status.negative,
    bgColor: '#fef2f2',
  },
  COMMISSION: {
    label: 'Commission',
    color: colors.status.tertiary || '#179DB5',
    bgColor: '#f0f9fb',
  },
} as const;

const formatCurrency = (amount: number | string): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return '$' + Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).toUpperCase();
};

const PaymentMethodIcon: React.FC<{ method?: string | null }> = ({ method }) => {
  switch (method) {
    case 'WIRE': return <BankOutlined />;
    case 'CHECK': return <CreditCardOutlined />;
    case 'ACH': return <BankOutlined />;
    default: return <DollarOutlined />;
  }
};

const StatusIndicator: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
    PAID: { icon: <CheckCircleOutlined />, color: colors.status.positive, label: 'Paid' },
    PENDING: { icon: <ClockCircleOutlined />, color: colors.status.warning, label: 'Pending' },
    PARTIAL: { icon: <MinusCircleOutlined />, color: '#e6851b', label: 'Partial' },
    VOIDED: { icon: <CloseCircleOutlined />, color: colors.text.tertiary, label: 'Voided' },
  };
  const c = config[status] || config.PENDING;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: c.color, fontSize: 12 }}>
      {c.icon} {c.label}
    </span>
  );
};

const TransactionRow: React.FC<{ tx: Transaction }> = ({ tx }) => {
  const amount = typeof tx.amount === 'string' ? parseFloat(tx.amount) : tx.amount;
  const typeLabel = tx.transactionType === 'CREDIT_NOTE' ? 'Credit Note' : tx.transactionType === 'INVOICE' ? 'Invoice' : 'Payment';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '28px 1fr auto auto',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      backgroundColor: '#fafafa',
      borderRadius: 6,
      marginBottom: 4,
      fontSize: 13,
    }}>
      <span style={{ color: '#999', display: 'flex', alignItems: 'center' }}>
        <PaymentMethodIcon method={tx.paymentMethod} />
      </span>
      <div>
        <div style={{ fontWeight: 500 }}>
          {typeLabel}
          {tx.reference && <span style={{ color: colors.text.secondary, marginLeft: 6 }}>#{tx.reference}</span>}
          {tx.legacyTable && (
            <Tag style={{ fontSize: 9, padding: '0 4px', lineHeight: '14px', marginLeft: 6 }}>Legacy</Tag>
          )}
        </div>
        <div style={{ fontSize: 11, color: colors.text.tertiary }}>
          {formatDate(tx.date)}
          {tx.description && ` — ${tx.description}`}
        </div>
      </div>
      <StatusIndicator status={tx.status} />
      <div style={{
        fontWeight: 600,
        fontFamily: 'monospace',
        fontSize: 14,
        textAlign: 'right',
        minWidth: 90,
      }}>
        {formatCurrency(amount)}
      </div>
    </div>
  );
};

export const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
  open,
  onClose,
  transactions,
  shipmentRef,
}) => {
  const grouped = {
    BUYER_RECEIVABLE: transactions.filter(t => t.direction === 'BUYER_RECEIVABLE'),
    SUPPLIER_PAYABLE: transactions.filter(t => t.direction === 'SUPPLIER_PAYABLE'),
    COMMISSION: transactions.filter(t => t.direction === 'COMMISSION'),
  };

  const directionOrder: Array<keyof typeof DIRECTION_CONFIG> = [
    'BUYER_RECEIVABLE',
    'SUPPLIER_PAYABLE',
    'COMMISSION',
  ];

  return (
    <Modal
      title={
        <span style={{ fontSize: 16, fontWeight: 600 }}>
          Transaction Details{shipmentRef ? ` — ${shipmentRef}` : ''}
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={640}
      styles={{ body: { padding: '16px 24px', maxHeight: '70vh', overflow: 'auto' } }}
    >
      {transactions.length === 0 ? (
        <Empty description="No transactions" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        directionOrder.map((direction) => {
          const txs = grouped[direction];
          if (txs.length === 0) return null;
          const config = DIRECTION_CONFIG[direction];
          const total = txs.reduce((sum, t) => {
            const amt = typeof t.amount === 'string' ? parseFloat(t.amount) : t.amount;
            return sum + amt;
          }, 0);

          return (
            <div key={direction} style={{ marginBottom: 20 }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                backgroundColor: config.bgColor,
                borderLeft: `3px solid ${config.color}`,
                borderRadius: '0 6px 6px 0',
                marginBottom: 8,
              }}>
                <span style={{ fontWeight: 600, color: config.color, fontSize: 13 }}>
                  {config.label}
                  <Tag style={{ marginLeft: 8, fontSize: 10 }}>{txs.length}</Tag>
                </span>
                <span style={{ fontWeight: 700, fontFamily: 'monospace', color: config.color }}>
                  {formatCurrency(total)}
                </span>
              </div>
              {txs.map(tx => <TransactionRow key={tx.id} tx={tx} />)}
            </div>
          );
        })
      )}
    </Modal>
  );
};

export default TransactionDetailModal;
